import { Grid } from '../core/Grid.js';
import {
    Position,
    positionKey,
    parsePositionKey,
} from '../core/GridGeometry.js';
import {
    collectGateCellsFromPaths,
    floodFillRegions,
    buildDigitTree,
} from './vortexNetwork.js';

export interface VortexValidation {
    isValid: boolean;
    allGatesLinked: boolean;
    digitsConnected: boolean;
}

export class VortexHub {
    private grid: Grid;
    private gates: Set<string> = new Set();    // positionKey set

    // Persistent gate connections
    private gatePaths: Map<string, Position[]> = new Map();   // pairKey -> path (first/last are gates)
    private gatePairings: Map<string, string> = new Map();    // posKey -> posKey (bidirectional)

    // Build mode state
    private buildPath: Position[] = [];
    private buildStartGateKey: string | null = null;

    // Computed state (updated after each mutation)
    private gateLinks: Set<string> = new Set();
    private gateCells: Set<string> = new Set();

    // Phase 2: Flood-fill regions
    private cellRegion: Map<string, number> = new Map();
    private regionDigits: Map<number, number[]> = new Map();
    private largestRegionId: number = -1;
    private digitRegionCount: number = 0;

    // Phase 3: Auto digit tree
    private autoDigitEdges: Set<string> = new Set();

    constructor(grid: Grid) {
        this.grid = grid;
        this.computeState();
    }

    // ── Getters ──────────────────────────────────────────────────

    getGrid(): Grid { return this.grid; }
    getGates(): Set<string> { return new Set(this.gates); }
    getGateLinks(): Set<string> { return new Set(this.gateLinks); }
    getAutoDigitEdges(): Set<string> { return new Set(this.autoDigitEdges); }
    getCellRegion(): Map<string, number> { return new Map(this.cellRegion); }
    getLargestRegionId(): number { return this.largestRegionId; }
    getGatePairings(): Map<string, string> { return new Map(this.gatePairings); }
    getGatePaths(): Map<string, Position[]> { return new Map(this.gatePaths); }
    getBuildPath(): Position[] { return [...this.buildPath]; }
    isBuilding(): boolean { return this.buildPath.length > 0; }
    getBuildStartGateKey(): string | null { return this.buildStartGateKey; }

    isGate(pos: Position): boolean {
        return this.gates.has(positionKey(pos));
    }

    isGateLinked(pos: Position): boolean {
        const key = positionKey(pos);
        const pairedKey = this.gatePairings.get(key);
        if (!pairedKey) return false;
        return this.gatePaths.has(this.pairKey(key, pairedKey));
    }

    isGatePaired(pos: Position): boolean {
        return this.gatePairings.has(positionKey(pos));
    }

    // ── Gate Pair Creation ──────────────────────────────────────

    /**
     * Can a cell host a fresh gate? Must be a valid cell with no digit and no
     * existing gate. A cell crossed by another pair's link path is allowed —
     * placing there breaks that link (see {@link createPair}).
     */
    canPlaceGate(pos: Position): boolean {
        if (!this.grid.isValidPosition(pos)) return false;
        const key = positionKey(pos);
        if (this.grid.hasDigitAt(pos)) return false;
        if (this.gates.has(key)) return false;
        return true;
    }

    /**
     * Create a gate pair from two empty cells. The pair starts paired-but-unlinked.
     * If either cell sits on another pair's link path, that link is destroyed
     * (its pairing is kept). Returns false if either cell is invalid or they coincide.
     */
    createPair(a: Position, b: Position): boolean {
        const keyA = positionKey(a);
        const keyB = positionKey(b);
        if (keyA === keyB) return false;
        if (!this.canPlaceGate(a) || !this.canPlaceGate(b)) return false;

        this.unlinkPathThroughCell(keyA);
        this.unlinkPathThroughCell(keyB);

        this.gates.add(keyA);
        this.gates.add(keyB);
        this.gatePairings.set(keyA, keyB);
        this.gatePairings.set(keyB, keyA);
        this.computeState();
        return true;
    }

    /** Remove the link path that passes through a cell (keeps the pairing). */
    private unlinkPathThroughCell(cellKey: string): void {
        const toUnlink: string[] = [];
        for (const [pk, path] of this.gatePaths) {
            if (path.some(p => positionKey(p) === cellKey)) toUnlink.push(pk);
        }
        for (const pk of toUnlink) this.gatePaths.delete(pk);
    }

    /** Remove an entire gate pair: both gates, their pairing, and their path. */
    removeGatePair(posKeyA: string): boolean {
        if (!this.gates.has(posKeyA)) return false;
        const posKeyB = this.gatePairings.get(posKeyA);
        if (!posKeyB) return false;

        this.gatePaths.delete(this.pairKey(posKeyA, posKeyB));
        this.gatePairings.delete(posKeyA);
        this.gatePairings.delete(posKeyB);
        this.gates.delete(posKeyA);
        this.gates.delete(posKeyB);

        this.computeState();
        return true;
    }

    /** Get all paired gates with serialized position strings (for usage scanning). */
    getGatePairsSerialized(): Array<{ keyA: string; keyB: string; serializedA: string; serializedB: string }> {
        const gs = this.grid.serializer;
        const pairs: Array<{ keyA: string; keyB: string; serializedA: string; serializedB: string }> = [];
        const seen = new Set<string>();
        for (const [keyA, keyB] of this.gatePairings) {
            const pk = this.pairKey(keyA, keyB);
            if (seen.has(pk)) continue;
            seen.add(pk);
            pairs.push({
                keyA,
                keyB,
                serializedA: gs.serializePosition(parsePositionKey(keyA)),
                serializedB: gs.serializePosition(parsePositionKey(keyB)),
            });
        }
        return pairs;
    }

    // ── Build Mode ──────────────────────────────────────────────

    /**
     * Select a gate's pair and enter build mode toward its fixed partner.
     * If the pair was already linked, its path is destroyed first.
     */
    startBuildFromGate(pos: Position): boolean {
        if (this.buildPath.length > 0) return false;
        const key = positionKey(pos);
        if (!this.gates.has(key)) return false;
        const pairedKey = this.gatePairings.get(key);
        if (!pairedKey) return false;

        // Destroy the existing link (keep the pairing)
        this.gatePaths.delete(this.pairKey(key, pairedKey));
        this.buildPath = [{ ...pos }];
        this.buildStartGateKey = key;
        this.computeState();
        return true;
    }

    /** The gate key of the partner of the gate that build mode started from. */
    getBuildPartnerKey(): string | null {
        if (!this.buildStartGateKey) return null;
        return this.gatePairings.get(this.buildStartGateKey) ?? null;
    }

    /** Extend the build path toward an aligned empty cell (same row or column). */
    extendBuild(pos: Position): boolean {
        if (this.buildPath.length === 0) return false;
        if (!this.grid.isValidPosition(pos)) return false;

        const last = this.buildPath[this.buildPath.length - 1];
        if (!this.isAligned(last, pos)) return false;

        const key = positionKey(pos);
        if (this.buildPath.some(p => positionKey(p) === key)) return false;

        // Validate all intermediate cells and the target
        const cellsBetween = this.getCellsBetween(last, pos);
        for (const cell of cellsBetween) {
            if (!this.isValidBuildCell(cell)) return false;
            if (this.buildPath.some(p => positionKey(p) === positionKey(cell))) return false;
        }
        if (!this.isValidBuildCell(pos)) return false;

        for (const cell of cellsBetween) {
            this.buildPath.push({ ...cell });
        }
        this.buildPath.push({ ...pos });
        return true;
    }

    /**
     * Finish the build by reaching the start gate's fixed partner, aligned with
     * the path end. Pairing already exists; this only stores the link path.
     */
    finishBuild(targetPos: Position): boolean {
        if (this.buildPath.length === 0) return false;

        const last = this.buildPath[this.buildPath.length - 1];
        if (!this.isAligned(last, targetPos)) return false;

        const targetKey = positionKey(targetPos);
        const startKey = this.buildStartGateKey!;
        // Can only link to the start gate's fixed partner — no relinking
        if (this.gatePairings.get(startKey) !== targetKey) return false;

        // Validate all intermediate cells (between last path cell and target gate)
        const cellsBetween = this.getCellsBetween(last, targetPos);
        for (const cell of cellsBetween) {
            if (!this.isValidBuildCell(cell)) return false;
            if (this.buildPath.some(p => positionKey(p) === positionKey(cell))) return false;
        }

        // Store path (including intermediate cells and target gate)
        const fullPath = [...this.buildPath, ...cellsBetween.map(c => ({ ...c })), { ...targetPos }];
        this.gatePaths.set(this.pairKey(startKey, targetKey), fullPath);

        // Clear build state
        this.buildPath = [];
        this.buildStartGateKey = null;

        this.computeState();
        return true;
    }

    /**
     * Extend the build onto the start gate's fixed partner, so the path visually
     * reaches it — used by the keyboard trace, which holds on the partner before
     * committing (the link is stored by {@link commitBuildToPartner} on release). The
     * mouse commits in one step via {@link finishBuild} instead. Returns false unless
     * `pos` is the partner, aligned, and every cell up to it is a legal build cell.
     */
    extendBuildToPartner(pos: Position): boolean {
        if (this.buildPath.length === 0) return false;
        const key = positionKey(pos);
        if (this.gatePairings.get(this.buildStartGateKey!) !== key) return false;

        const last = this.buildPath[this.buildPath.length - 1];
        if (!this.isAligned(last, pos)) return false;
        const cellsBetween = this.getCellsBetween(last, pos);
        for (const cell of cellsBetween) {
            if (!this.isValidBuildCell(cell)) return false;
            if (this.buildPath.some(p => positionKey(p) === positionKey(cell))) return false;
        }

        for (const cell of cellsBetween) this.buildPath.push({ ...cell });
        this.buildPath.push({ ...pos });
        return true;
    }

    /**
     * Store the current build path as the link, when it already ends on the start
     * gate's partner (via {@link extendBuildToPartner}). The trace's commit-on-release.
     */
    commitBuildToPartner(): boolean {
        if (this.buildPath.length < 2) return false;
        const startKey = this.buildStartGateKey!;
        const lastKey = positionKey(this.buildPath[this.buildPath.length - 1]);
        if (this.gatePairings.get(startKey) !== lastKey) return false;

        this.gatePaths.set(this.pairKey(startKey, lastKey), this.buildPath.map(p => ({ ...p })));
        this.buildPath = [];
        this.buildStartGateKey = null;
        this.computeState();
        return true;
    }

    /**
     * Seed a build path directly, bypassing gate/pairing validation.
     * For tutorial simulations that animate a path without real gates.
     */
    seedBuildPath(positions: Position[]): void {
        if (positions.length === 0) return;
        this.buildPath = positions.map(p => ({ ...p }));
        this.buildStartGateKey = positionKey(positions[0]);
    }

    /** Cancel the current build. */
    cancelBuild(): void {
        this.buildPath = [];
        this.buildStartGateKey = null;
    }

    /** Backtrack: undo last step in build path. */
    backtrackBuild(): boolean {
        if (this.buildPath.length <= 1) return false;
        this.buildPath.pop();
        return true;
    }

    // ── Helpers ──────────────────────────────────────────────────

    private pairKey(a: string, b: string): string {
        return a < b ? `${a}|${b}` : `${b}|${a}`;
    }

    private isAligned(a: Position, b: Position): boolean {
        return (a.row === b.row || a.col === b.col) &&
            !(a.row === b.row && a.col === b.col);
    }

    /** Get cells between two aligned positions (exclusive of both endpoints), in order from a toward b. */
    private getCellsBetween(a: Position, b: Position): Position[] {
        const cells: Position[] = [];
        if (a.row === b.row) {
            const step = a.col < b.col ? 1 : -1;
            for (let col = a.col + step; col !== b.col; col += step) {
                cells.push({ row: a.row, col });
            }
        } else {
            const step = a.row < b.row ? 1 : -1;
            for (let row = a.row + step; row !== b.row; row += step) {
                cells.push({ row, col: a.col });
            }
        }
        return cells;
    }

    /** Whether the build path may pass through `pos` — no digit, no gate, and not a
     *  cell some other link's path already uses. Public face of {@link isValidBuildCell}. */
    canBuildThrough(pos: Position): boolean {
        return this.isValidBuildCell(pos);
    }

    /** Check if a cell is valid for building through (no digit, no gate, no existing gate path). */
    private isValidBuildCell(pos: Position): boolean {
        if (!this.grid.isValidPosition(pos)) return false;
        const key = positionKey(pos);
        if (this.grid.hasDigitAt(pos)) return false;
        if (this.gates.has(key)) return false;
        if (this.gateCells.has(key)) return false;
        return true;
    }

    /**
     * Enforce the "no lone gates" invariant: drop any gate without a pairing,
     * and any pairing/path whose endpoints are not both present.
     */
    private pruneLoneGates(): void {
        // Drop pairings referencing a missing gate
        for (const [key, pairedKey] of [...this.gatePairings]) {
            if (!this.gates.has(key) || !this.gates.has(pairedKey)) {
                this.gatePairings.delete(key);
            }
        }
        // Drop gates with no pairing
        for (const key of [...this.gates]) {
            if (!this.gatePairings.has(key)) {
                this.gates.delete(key);
            }
        }
        // Drop paths whose pair is gone
        for (const pk of [...this.gatePaths.keys()]) {
            const [a, b] = pk.split('|');
            if (this.gatePairings.get(a) !== b) {
                this.gatePaths.delete(pk);
            }
        }
    }

    // ── 3-Phase State Computation ───────────────────────────────

    /** Compute all rendering and validation state. */
    private computeState(): void {
        this.gateLinks.clear();
        this.gateCells.clear();
        this.autoDigitEdges.clear();
        this.cellRegion.clear();
        this.regionDigits.clear();
        this.largestRegionId = -1;
        this.digitRegionCount = 0;

        // Phase 1: Collect gate cells and edges from paths
        if (this.gatePaths.size > 0) {
            const result = collectGateCellsFromPaths(this.grid, this.gatePaths);
            this.gateCells = result.gateCells;
            this.gateLinks = result.gateLinks;
        }

        // Phase 2: Flood-fill regions
        const floodResult = floodFillRegions(this.grid, this.gateCells);
        this.cellRegion = floodResult.cellRegion;
        this.regionDigits = floodResult.regionDigits;

        let maxDigitCount = 0;
        for (const [regionId, digits] of floodResult.regionDigits) {
            this.digitRegionCount++;
            if (digits.length > maxDigitCount) {
                maxDigitCount = digits.length;
                this.largestRegionId = regionId;
            }
        }

        // Phase 3: Auto digit tree per region
        for (const [regionId, digits] of floodResult.regionDigits) {
            if (digits.length < 2) continue;
            const regionCells = floodResult.regionCells.get(regionId)!;
            const treeResult = buildDigitTree(this.grid, regionCells, digits);
            for (const e of treeResult.edges) {
                this.autoDigitEdges.add(e);
            }
        }
    }

    // ── Validation ──────────────────────────────────────────────

    validate(): VortexValidation {
        const allGatesLinked = this.gates.size === 0 ||
            [...this.gates].every(gateKey => {
                const pairedKey = this.gatePairings.get(gateKey);
                if (!pairedKey) return false;
                return this.gatePaths.has(this.pairKey(gateKey, pairedKey));
            });

        const digitsConnected = this.digitRegionCount <= 1;

        return {
            isValid: allGatesLinked && digitsConnected,
            allGatesLinked,
            digitsConnected,
        };
    }

    /** Get linked gate pairs (those with paths). */
    getGatePairs(): Array<[Position, Position]> {
        const pairs: Array<[Position, Position]> = [];
        for (const [, path] of this.gatePaths) {
            pairs.push([path[0], path[path.length - 1]]);
        }
        return pairs;
    }

    /** Get all gate pairs (linked and unlinked). */
    getAllGatePairs(): Array<[Position, Position]> {
        const pairs: Array<[Position, Position]> = [];
        const seen = new Set<string>();
        for (const [key, pairedKey] of this.gatePairings) {
            const pk = this.pairKey(key, pairedKey);
            if (seen.has(pk)) continue;
            seen.add(pk);
            pairs.push([parsePositionKey(key), parsePositionKey(pairedKey)]);
        }
        return pairs;
    }

    /** Set of pair ids (canonical "keyA|keyB") for pairs that are linked. */
    getLinkedPairIds(): Set<string> {
        return new Set(this.gatePaths.keys());
    }

    // ── Serialization ───────────────────────────────────────────

    /** Format: "G:A1,B7,...;P:A1.B2.C2.D7;R:A1=B7" */
    /**
     * Compact format: comma-separated entries.
     *   Linked pair path: concatenated positions (e.g. C2C3D3D4)
     *   Unlinked pair: posA=posB (e.g. A1=B7)
     *   Lone gate: single position (e.g. H6)
     */
    serialize(): string {
        const gs = this.grid.serializer;
        const entries: string[] = [];

        // Linked paths: concatenated positions
        for (const [, path] of this.gatePaths) {
            entries.push(path.map(p => gs.serializePosition(p)).join(''));
        }

        // Unlinked pairings: posA=posB
        const seenPairs = new Set<string>();
        for (const [key, pairedKey] of this.gatePairings) {
            const pk = this.pairKey(key, pairedKey);
            if (seenPairs.has(pk)) continue;
            seenPairs.add(pk);
            if (!this.gatePaths.has(pk)) {
                const posA = gs.serializePosition(parsePositionKey(key));
                const posB = gs.serializePosition(parsePositionKey(pairedKey));
                entries.push(`${posA}=${posB}`);
            }
        }

        // Lone gates: not in any path or pairing
        const coveredGates = new Set<string>();
        for (const [, path] of this.gatePaths) {
            coveredGates.add(positionKey(path[0]));
            coveredGates.add(positionKey(path[path.length - 1]));
        }
        for (const [key] of this.gatePairings) {
            coveredGates.add(key);
        }
        for (const gateKey of this.gates) {
            if (!coveredGates.has(gateKey)) {
                entries.push(gs.serializePosition(parsePositionKey(gateKey)));
            }
        }

        return entries.join(',');
    }

    /**
     * A direction- and order-independent form of a compact hub string. Two
     * functionally identical hubs — same pairs and routes, but built/serialized in
     * a different order or a link traced from the other end — produce the same
     * canonical string. Used to tell whether an edit *actually* changed the Hub
     * (e.g. whether to still offer "Restore backup").
     */
    static canonical(serialized: string): string {
        if (!serialized) return '';
        const entries = serialized.split(',').filter(Boolean).map(entry => {
            if (entry.includes('=')) {
                // Unlinked pair: order the two endpoints.
                const [a, b] = entry.split('=');
                return a <= b ? `${a}=${b}` : `${b}=${a}`;
            }
            if (entry.length <= 2) return entry; // lone gate
            // Linked path: a route and its reverse are the same link.
            const cells = entry.match(/.{2}/g) ?? [entry];
            const forward = cells.join('');
            const reversed = [...cells].reverse().join('');
            return forward <= reversed ? forward : reversed;
        });
        entries.sort();
        return entries.join(',');
    }

    deserialize(serialized: string): void {
        this.gates.clear();
        this.gatePaths.clear();
        this.gatePairings.clear();
        this.buildPath = [];
        this.buildStartGateKey = null;

        if (!serialized) {
            this.computeState();
            return;
        }

        // Detect legacy G:/P:/R: format vs compact format
        if (serialized.includes('G:') || serialized.includes('P:') || serialized.includes('R:')) {
            this.deserializeLegacy(serialized);
        } else {
            this.deserializeCompact(serialized);
        }

        // Enforce the "no lone gates" invariant on any legacy/imported data
        this.pruneLoneGates();
        this.computeState();
    }

    private deserializeCompact(serialized: string): void {
        const gs = this.grid.serializer;

        for (const entry of serialized.split(',')) {
            if (!entry) continue;
            try {
                if (entry.includes('=')) {
                    // Unlinked pair
                    const eqIdx = entry.indexOf('=');
                    const posA = gs.deserializePosition(entry.slice(0, eqIdx));
                    const posB = gs.deserializePosition(entry.slice(eqIdx + 1));
                    const keyA = positionKey(posA);
                    const keyB = positionKey(posB);
                    this.gates.add(keyA);
                    this.gates.add(keyB);
                    this.gatePairings.set(keyA, keyB);
                    this.gatePairings.set(keyB, keyA);
                } else if (entry.length === 2) {
                    // Lone gate
                    const pos = gs.deserializePosition(entry);
                    this.gates.add(positionKey(pos));
                } else {
                    // Linked pair path: split into 2-char position chunks
                    const cells: Position[] = [];
                    for (let i = 0; i < entry.length; i += 2) {
                        cells.push(gs.deserializePosition(entry.slice(i, i + 2)));
                    }
                    if (cells.length >= 2) {
                        const startKey = positionKey(cells[0]);
                        const endKey = positionKey(cells[cells.length - 1]);
                        this.gates.add(startKey);
                        this.gates.add(endKey);
                        const pk = this.pairKey(startKey, endKey);
                        this.gatePaths.set(pk, cells);
                        this.gatePairings.set(startKey, endKey);
                        this.gatePairings.set(endKey, startKey);
                    }
                }
            } catch {
                // Invalid entry, skip
            }
        }
    }

    private deserializeLegacy(serialized: string): void {
        const gs = this.grid.serializer;
        const sections = serialized.split(';');

        for (const section of sections) {
            if (section.startsWith('G:')) {
                for (const part of section.slice(2).split(',')) {
                    if (!part) continue;
                    const pos = gs.deserializePosition(part);
                    this.gates.add(positionKey(pos));
                }
            } else if (section.startsWith('P:')) {
                for (const pathStr of section.slice(2).split(',')) {
                    if (!pathStr) continue;
                    const cells = pathStr.split('.').map(s => gs.deserializePosition(s));
                    if (cells.length >= 2) {
                        const startKey = positionKey(cells[0]);
                        const endKey = positionKey(cells[cells.length - 1]);
                        const pk = this.pairKey(startKey, endKey);
                        this.gatePaths.set(pk, cells);
                        this.gatePairings.set(startKey, endKey);
                        this.gatePairings.set(endKey, startKey);
                    }
                }
            } else if (section.startsWith('R:')) {
                for (const pairStr of section.slice(2).split(',')) {
                    const eqIdx = pairStr.indexOf('=');
                    if (eqIdx < 0) continue;
                    try {
                        const posA = gs.deserializePosition(pairStr.slice(0, eqIdx));
                        const posB = gs.deserializePosition(pairStr.slice(eqIdx + 1));
                        const keyA = positionKey(posA);
                        const keyB = positionKey(posB);
                        this.gatePairings.set(keyA, keyB);
                        this.gatePairings.set(keyB, keyA);
                    } catch {
                        // Invalid format, skip
                    }
                }
            }
            // Old 'L:' sections are silently ignored
        }
    }

    reset(): void {
        this.gates.clear();
        this.gatePaths.clear();
        this.gatePairings.clear();
        this.buildPath = [];
        this.buildStartGateKey = null;
        this.computeState();
    }

    isStarted(): boolean {
        return this.gates.size > 0 || this.gatePaths.size > 0;
    }
}
